import React, { useEffect, useReducer } from "react";
import AuthContext from "./authContext";
import authReducer from "./authReducer";
import { API_BASE, apiErrorMessage, authedFetch } from "../../utils/api";

import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  USER_LOADED,
  AUTH_ERROR,
  UPDATE_USER,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
  CLEAR_AUTH_MESSAGES,
  UPDATE_USER_COMPLETE,
} from "../types";

const AuthState = (props) => {
  const initialState = {
    token: localStorage.getItem("token"), // token is stored in localStorage of DOM by default
    isAuthenticated: null,
    loading: true,
    user: {
      first_name: "",
      last_name: "",
      avatar: null,
    },
    error: null,
    userUpdated: false,
  };

  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load User
  const loadUser = async () => {
    const res = await authedFetch(`${API_BASE}/auth`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      // payload is {status: "success", token: "token", user_id: "user"}
      dispatch({ type: USER_LOADED, payload: res_json });
    } else {
      if (res.status === 401) {
        dispatch({ type: AUTH_ERROR, payload: "Unauthorized" });
      } else {
        dispatch({ type: AUTH_ERROR, payload: apiErrorMessage(res_json) });
      }
    }
  };

  // Register User
  const register = async (newUser) => {
    const formData = new FormData();
    formData.append("user_id", newUser.user_id);
    formData.append("password", newUser.password);
    if (newUser.name) formData.append("name", newUser.name);
    if (newUser.first_name) formData.append("first_name", newUser.first_name);
    if (newUser.last_name) formData.append("last_name", newUser.last_name);
    if (newUser.avatar && newUser.avatar instanceof File) {
      formData.append("avatar", newUser.avatar);
    }
    const res = await fetch(`${API_BASE}/user`, {
      method: "POST",
      body: formData,
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: REGISTER_SUCCESS, payload: res_json });
      loadUser();
    } else {
      dispatch({
        type: REGISTER_FAIL,
        payload: apiErrorMessage(res_json),
      });
    }
  };

  // Login User
  const login = async (returnedUser) => {
    const formData = new FormData();
    formData.append("user_id", returnedUser.user_id);
    formData.append("password", returnedUser.password);
    formData.append("first_name", returnedUser.first_name);
    formData.append("last_name", returnedUser.last_name);
    const res = await fetch(`${API_BASE}/auth`, {
      method: "POST",
      body: formData,
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: LOGIN_SUCCESS, payload: res_json });
      loadUser();
    } else {
      dispatch({
        type: LOGIN_FAIL,
        payload: apiErrorMessage(res_json),
      });
    }
  };

  const update = async (updatedUser) => {
    const formData = new FormData();
    if (updatedUser.password) formData.append("password", updatedUser.password);
    if (updatedUser.name) formData.append("name", updatedUser.name);
    if (updatedUser.first_name) formData.append("first_name", updatedUser.first_name);
    if (updatedUser.last_name) formData.append("last_name", updatedUser.last_name);
    if (updatedUser.avatar && updatedUser.avatar instanceof File) {
      formData.append("avatar", updatedUser.avatar);
    }
    const res = await authedFetch(`${API_BASE}/user`, {
      method: "PUT",
      body: formData,
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: UPDATE_USER, payload: res_json });
    } else {
      dispatch({
        type: AUTH_ERROR,
        payload: apiErrorMessage(res_json),
      });
    }
  };

  const updateUserComplete = () => {
    dispatch({ type: UPDATE_USER_COMPLETE });
  };

  // Logout: tell the BE to revoke the refresh token (best-effort — if
  // the network is down we still drop local state so the UI goes to
  // the logged-out state), then clear reducer state.
  const logout = () => {
    const refreshToken = localStorage.getItem("refresh_token");
    // Clear local state immediately so the UI reacts before the
    // network round-trip completes.
    dispatch({ type: LOGOUT });
    // Best-effort server-side revocation — fire and forget.
    if (refreshToken) {
      const form = new FormData();
      form.append("refresh_token", refreshToken);
      fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        body: form,
      }).catch(() => {});
    }
  };

  // Refresh failures emit `auth:expired` from utils/api — dispatch
  // LOGOUT so the UI moves to the login screen. Without this the user
  // would see stale data until the next render cycle happened to call
  // an authed endpoint.
  useEffect(() => {
    const onExpired = () => dispatch({ type: LOGOUT });
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  // Clear Errors
  const clearAuthMessages = () => dispatch({ type: CLEAR_AUTH_MESSAGES });

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        loading: state.loading,
        message: state.message,
        user: state.user,
        userUpdated: state.userUpdated,
        register,
        loadUser,
        login,
        logout,
        update,
        updateUserComplete,
        clearAuthMessages,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthState;
